"use client";

import { cn } from "@/lib/utils";
import { useAppData } from "@/store/appStore";
import React, { useEffect, useState, useRef } from "react";
import { HomeIcon, LogOut, UserCircle2 } from "lucide-react";
import Link from "next/link";
import { guestLogin } from "@/app/product/_actions/guest.login";
import { logout as logoutAction } from "@/app/product/_actions/user.actions";
import { getClearedProductListFilters } from "@/lib/utils/product-list-filters";

interface UserAction {
	id: string;
	name: string;
	action?: () => Promise<void>;
	href?: string;
	icon?: React.ElementType;
}

const UserSelector = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [, setForceRender] = useState<boolean>();
	const [appData, setData, setCookielessData] = useAppData();
	const containerRef = useRef<HTMLDivElement>(null);

	const handleToggle = () => {
		setIsOpen(!isOpen);
	};

	const handleClickOutside = (event: MouseEvent) => {
		if (
			containerRef.current &&
			!containerRef.current.contains(event.target as Node)
		) {
			setIsOpen(false);
		}
	};

	useEffect(() => {
		setForceRender(true);
	}, [appData.user]);

	useEffect(() => {
		document.addEventListener("click", handleClickOutside);
		return () => {
			document.removeEventListener("click", handleClickOutside);
		};
	}, []);

	const handleLogout = async () => {
		// Call the logout action
		const result = await logoutAction();

		if (
			result &&
			typeof result === "object" &&
			"success" in result &&
			result.success
		) {
			// Update appData
			setData({
				authorization: undefined, // Remove Authorization token
				email: "",
			});
			setCookielessData({
				user: null,
				isUserReady: true,
				isExistingUser: undefined,
				...getClearedProductListFilters(false),
			});
			sessionStorage.removeItem("isExistingUser");
			sessionStorage.removeItem("onboarding_visited");
			// Perform guest login
			await guestLogin(appData.deviceId, null);

			setIsOpen(false);
			// Hard reload rather than router.refresh()+push - guarantees every
			// client-side store/config/product-list piece of state re-initializes
			// fresh against the new (logged-out/guest) cookies, instead of relying
			// on each consumer's own effects to correctly react to the change.
			window.location.href = "/";
		}
	};

	const userActions: UserAction[] = [
		{
			id: "header_home",
			name: "Home",
			href: "/",
			icon: HomeIcon,
		},
		{
			id: "header_profile",
			name: "Profile",
			href: "/profile",
			icon: UserCircle2,
		},
	];

	if (appData?.user) {
		userActions.push({
			id: "header_logout",
			name: "Logout",
			action: handleLogout,
			icon: LogOut,
		});
	}

	const handleActionClick = async (actionItem: UserAction) => {
		setIsOpen(false);
		if (actionItem.action) {
			await actionItem.action();
		}
		// Navigation for href is handled by Link component directly
	};

	return (
		<div
			ref={containerRef}
			className={cn("relative inline-block", isOpen && "z-[999999]")}
		>
			<div
				className="flex items-center gap-2 cursor-pointer mr-2"
				onClick={handleToggle}
			>
				<div
					className={
						"am-accent-outline flex justify-center items-center h-[42px] w-[42px] flex-shrink-0 rounded-[10px]"
					}
				>
					<span className={"am-accent-text text-sm font-medium"}>
						{appData?.user?.firstName && appData?.user?.lastName
							? `${appData.user.firstName.charAt(0).toUpperCase()}${appData.user.lastName.charAt(0).toUpperCase()}`
							: appData?.user?.firstName
								? appData.user.firstName.substring(0, 2).toUpperCase()
								: "U"}
					</span>
				</div>
			</div>

			{isOpen && (
				<div className="absolute top-16 right-2 flex flex-col bg-automint-dark rounded-lg shadow-md z-[999999] w-max min-w-full max-w-[200px] user-selector text-sm overflow-hidden">
					{appData?.isUserReady && appData?.user ? (
						<>
							<div className="px-4 py-3 text-automint-neon font-medium rounded-t-lg flex items-center gap-2 pointer-events-none bg-gray-700/40 overflow-hidden">
								<span className="truncate max-w-[150px]">
									{appData.user.firstName} {appData.user.lastName || ""}
								</span>
							</div>
							{userActions?.map((item: UserAction, i: number) => {
								const content = (
									<div
										className={cn(
											"px-4 py-3 text-automint-neon hover:bg-gray-700/40 cursor-pointer flex items-center gap-2 overflow-hidden",
											i === userActions?.length - 1 && "rounded-b-lg",
											item.id === "header_logout" &&
												"text-red-400 hover:bg-gray-700/40", // Red color for logout
										)}
										onClick={() => handleActionClick(item)}
									>
										{item.icon && (
											<item.icon size={16} className="flex-shrink-0" />
										)}
										<span className="truncate">{item.name}</span>
									</div>
								);

								if (item.href && !item.action) {
									return (
										<Link href={item.href} key={item.id} passHref>
											{content}
										</Link>
									);
								}
								return <div key={item.id}>{content}</div>;
							})}
						</>
					) : (
						<Link
							href="/authenticate/email"
							className="flex items-center gap-2 px-4 py-3 text-automint-neon  cursor-pointer truncate bg-automint-black"
							onClick={() => {
								setIsOpen(false);
							}}
						>
							Login
						</Link>
					)}
				</div>
			)}
		</div>
	);
};

export default UserSelector;
